import { useEffect, useState } from "react";
import { api, apiError, eur } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { Empty, FormDialog, ListCard } from "@/components/Crud";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Plus, Trash2, Receipt, FileDown, Mail, ChevronDown } from "lucide-react";

export default function VenditeDirette() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [lineFor, setLineFor] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [msg, setMsg] = useState("");

  const load = () => api.get("/direct-sales").then(({ data }) => setRows(data));

  useEffect(() => {
    load();
    api.get("/customers").then(({ data }) => setCustomers(data));
    api.get("/products").then(({ data }) => setProducts(data));
  }, []);

  const newFields = [
    {
      name: "customer_id", label: "Cliente", type: "select", required: true, full: true,
      options: customers.map((c) => ({ value: c.id, label: c.name })),
    },
    {
      name: "product_id", label: "Articolo", type: "select", full: true,
      options: products.map((p) => ({ value: p.id, label: `${p.code} — ${p.name} (${eur(p.price)})` })),
    },
    { name: "quantity", label: "Quantità", type: "number" },
    { name: "unit_price", label: "Prezzo unitario €", type: "number" },
    { name: "notes", label: "Note", type: "textarea", full: true },
  ];

  const lineFields = [
    {
      name: "product_id", label: "Articolo da catalogo", type: "select", full: true,
      options: products.map((p) => ({ value: p.id, label: `${p.code} — ${p.name} (${eur(p.price)})` })),
    },
    { name: "name", label: "Descrizione (se fuori catalogo)", full: true },
    { name: "quantity", label: "Quantità", type: "number" },
    { name: "unit_price", label: "Prezzo unitario €", type: "number" },
  ];

  const save = async (values) => {
    try {
      await api.post("/direct-sales", {
        customer_id: values.customer_id, notes: values.notes,
        lines: [{ product_id: values.product_id, quantity: values.quantity, unit_price: values.unit_price }],
      });
      await load();
    } catch (e) {
      throw new Error(apiError(e.response?.data?.detail));
    }
  };

  const addLine = async (values) => {
    try {
      await api.post(`/direct-sales/${lineFor.id}/lines`, values);
      await load();
    } catch (e) {
      throw new Error(apiError(e.response?.data?.detail));
    }
  };

  const doInvoice = async (s) => {
    try {
      await api.post(`/direct-sales/${s.id}/invoice`);
      setMsg(`Vendita ${s.code} fatturata.`);
      await load();
    } catch (e) {
      setMsg(apiError(e.response?.data?.detail));
    }
  };

  const downloadPdf = async (s) => {
    try {
      const { data } = await api.get(`/direct-sales/${s.id}/invoice.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${s.invoice_code || "proforma"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setMsg("Download della proforma non riuscito.");
    }
  };

  const sendInvoice = async (s) => {
    try {
      const { data } = await api.post(`/direct-sales/${s.id}/send-invoice`);
      setMsg(`Proforma ${s.invoice_code} inviata a ${data.sent_to}.`);
    } catch (e) {
      setMsg(apiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="vendite-dirette-page">
      <PageHeader title="Vendite dirette" subtitle={`${rows.length} vendite · DPI e vestiario`}>
        <Button className="h-11 rounded-sm" onClick={() => setOpen(true)} data-testid="new-sale-btn">
          <Plus className="h-4 w-4 mr-2" /> Nuova vendita
        </Button>
      </PageHeader>

      {msg && <p className="mb-4 text-sm font-medium text-primary" data-testid="sale-message">{msg}</p>}

      <div className="space-y-4" data-testid="vendite-list">
        {rows.map((s) => (
          <Card key={s.id} className="rounded-sm shadow-none overflow-hidden">
            <button
              type="button"
              className="w-full text-left px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-3"
              onClick={() => setExpanded(expanded === s.id ? null : s.id)}
              data-testid={`expand-sale-${s.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs text-muted-foreground">{s.code}</span>
                  <p className="font-semibold">{s.customer_name}</p>
                  {s.invoice_status && (
                    <Badge variant={s.invoice_status === "incassato" ? "secondary" : "default"}
                           className="rounded-sm capitalize">
                      {s.invoice_status.replace("_", " ")}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1">{s.lines?.length || 0} articoli</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-display text-xl font-black tracking-tighter">{eur(s.totals?.total)}</span>
                <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${expanded === s.id ? "rotate-180" : ""}`} />
              </div>
            </button>

            {expanded === s.id && (
              <div className="border-t border-border px-4 sm:px-6 py-4" data-testid={`sale-detail-${s.id}`}>
                <div className="divide-y divide-border">
                  {(s.lines || []).map((l) => (
                    <div key={l.id} className="py-2 flex items-center justify-between gap-3 text-sm">
                      <p className="font-medium">{l.name} · {l.quantity} × {eur(l.unit_price)}</p>
                      <div className="flex items-center gap-3">
                        <span className="font-mono">{eur(l.quantity * l.unit_price)}</span>
                        {s.invoice_status === "da_fatturare" && (
                          <Button variant="ghost" size="icon" className="text-destructive"
                                  onClick={async () => { await api.delete(`/direct-sale-lines/${l.id}`); load(); }}
                                  data-testid={`delete-sale-line-${l.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  {(!s.invoice_status || s.invoice_status === "da_fatturare") && (
                    <Button size="sm" className="rounded-sm" onClick={() => setLineFor(s)}
                            data-testid={`add-sale-line-${s.id}`}>
                      <Plus className="h-3.5 w-3.5 mr-2" /> Aggiungi voce
                    </Button>
                  )}
                  {user?.role === "azienda" && s.invoice_status === "da_fatturare" && (
                    <Button variant="outline" size="sm" className="rounded-sm" onClick={() => doInvoice(s)}
                            data-testid={`invoice-sale-${s.id}`}>
                      <Receipt className="h-3.5 w-3.5 mr-2" /> Fattura
                    </Button>
                  )}
                  {user?.role === "azienda" && s.invoice_status && s.invoice_status !== "da_fatturare" && (
                    <>
                      <Button variant="outline" size="sm" className="rounded-sm" onClick={() => downloadPdf(s)}
                              data-testid={`sale-pdf-${s.id}`}>
                        <FileDown className="h-3.5 w-3.5 mr-2" /> PDF
                      </Button>
                      <Button variant="outline" size="sm" className="rounded-sm" onClick={() => sendInvoice(s)}
                              data-testid={`send-sale-invoice-${s.id}`}>
                        <Mail className="h-3.5 w-3.5 mr-2" /> Invia al cliente
                      </Button>
                    </>
                  )}
                  {s.invoice_status === "da_fatturare" && (
                    <Button variant="outline" size="sm" className="rounded-sm text-destructive"
                            onClick={async () => { await api.delete(`/direct-sales/${s.id}`); load(); }}
                            data-testid={`delete-sale-${s.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            )}
          </Card>
        ))}
        {rows.length === 0 && <ListCard><Empty text="Nessuna vendita diretta registrata." /></ListCard>}
      </div>

      <FormDialog open={open} onOpenChange={setOpen} title="Nuova vendita diretta" fields={newFields}
                  initial={{ quantity: 1 }} onSubmit={save} testid="sale-dialog" />
      <FormDialog open={!!lineFor} onOpenChange={(v) => !v && setLineFor(null)} title="Aggiungi voce"
                  fields={lineFields} initial={{ quantity: 1 }} onSubmit={addLine} testid="sale-line-dialog" />
    </div>
  );
}
